import { CustomerLead, api } from '@common';
import { useAuth } from '@hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';

export interface CreateCustomerLeadInput {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  serviceAddress: string;
  source?: string;
}

export interface UpdateCustomerLeadStatusInput {
  status: 'pending' | 'contacted' | 'qualified' | 'converted' | 'rejected';
  comments?: string;
}

export interface CustomerLeadsParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  orderBy?: string;
  orderType?: 'asc' | 'desc';
}

export interface PaginatedCustomerLeads {
  data: CustomerLead[];
  pagination: {
    totalCount: number;
    page: number;
    pageSize: number;
  };
}

export const getCustomerLeadsWithFilters = (params: CustomerLeadsParams) => {
  const { isAuthenticated } = useAuth();

  return useQuery<PaginatedCustomerLeads>({
    queryKey: ['customerLeads', params],
    queryFn: async () => {
      const response = await api.get('/customer-leads', { params });
      return response.data;
    },
    enabled: isAuthenticated,
  });
};

export const useCustomerLeads = () => {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();

  const getCustomerLeadById = (id: string) => {
    return useQuery<CustomerLead>({
      queryKey: ['customerLead', id],
      queryFn: () => api.get(`/customer-leads/${id}`).then((res) => res.data),
      enabled: !!id && isAuthenticated,
    });
  };

  const createCustomerLead = useMutation({
    mutationFn: (data: CreateCustomerLeadInput) =>
      api.post('/customer-leads', data).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customerLeads'] });
    },
  });

  const updateCustomerLeadStatus = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCustomerLeadStatusInput }) =>
      api.patch(`/customer-leads/${id}/status`, data).then((res) => res.data),
    onSuccess: (_data, variables) => {
      message.success('Lead status updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['customerLeads'] });
      queryClient.invalidateQueries({ queryKey: ['customerLead', variables.id] });
    },
  });

  const deleteCustomerLead = useMutation({
    mutationFn: (id: string) =>
      api.delete(`/customer-leads/${id}`).then((res) => res.data),
    onSuccess: () => {
      message.success('Lead deleted successfully!');
      queryClient.invalidateQueries({ queryKey: ['customerLeads'] });
    },
  });

  return {
    getCustomerLeadById,
    createCustomerLead,
    updateCustomerLeadStatus,
    deleteCustomerLead,
  };
};
